import { useParams, useNavigate } from 'react-router-dom';
import type { CSSProperties } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Typography,
  message,
  Dropdown,
  Spin,
  Table,
  Tag,
  Tooltip,
  Tabs,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  EnvironmentOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { PermissionButton } from '../components/common/PermissionButton';
import { usePermission } from '../hooks/usePermission';
import type { MenuProps } from 'antd';
import { trpc } from '../lib/api';
import { useMemo, useState } from 'react';
import { SupplierFormModal } from '../components/suppliers/SupplierFormModal';
import { CategoryTag } from '../components/common/CategoryTag';
import { UserDisplay } from '../components/common/UserDisplay';
import { useBreadcrumbTitle } from '../hooks/useBreadcrumb';
import { DocumentListSection } from '../components/document/DocumentListSection';
import { withDevModalFilePath } from '../components/common';

const { Title, Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/SupplierDetailPage.tsx');
const actionButtonStyle: CSSProperties = {
  height: 40,
  paddingInline: 16,
  borderRadius: 6,
};

type CategorySummary = {
  id: string;
  categoryLabel: string;
  color?: string | null;
  icon?: string | null;
};

interface RelatedEquipmentRow {
  id: string;
  name: string;
  category?: CategorySummary | null;
  supplierRole?: string | null;
}

interface RelatedPartRow {
  id: string;
  name: string;
  partNumber?: string | null;
  category?: CategorySummary | null;
  supplierRole?: string | null;
  currentStockLevel?: number | null;
}

interface RelatedPurchaseOrderRow {
  id: string;
  displayId?: string | null;
  orderDate?: string | null;
  status: string;
  totalAmount?: string | null;
}

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canManage, getDisabledMessage } = usePermission();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [messageApi, messageContextHolder] = message.useMessage();

  // 業者詳細取得
  const { data: supplier, isLoading, refetch } = trpc.suppliers.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  // パンくずリストに業者名を設定
  useBreadcrumbTitle('supplier', supplier?.name);

  // 関連設備を取得
  const { data: relatedEquipments } = trpc.suppliers.getRelatedEquipments.useQuery(
    { supplierId: id! },
    { enabled: !!id }
  );

  // 関連部品・消耗品を取得
  const { data: relatedParts } = trpc.suppliers.getRelatedParts.useQuery(
    { supplierId: id! },
    { enabled: !!id }
  );

  // 関連発注を取得
  const { data: relatedPurchaseOrders } = trpc.suppliers.getRelatedPurchaseOrders.useQuery(
    { supplierId: id! },
    { enabled: !!id }
  );

  const equipmentRows = useMemo<RelatedEquipmentRow[]>(
    () => (Array.isArray(relatedEquipments) ? (relatedEquipments as RelatedEquipmentRow[]) : []),
    [relatedEquipments]
  );
  const partRows = useMemo<RelatedPartRow[]>(
    () => (Array.isArray(relatedParts) ? (relatedParts as RelatedPartRow[]) : []),
    [relatedParts]
  );
  const purchaseOrderRows = useMemo<RelatedPurchaseOrderRow[]>(
    () =>
      Array.isArray(relatedPurchaseOrders)
        ? (relatedPurchaseOrders as RelatedPurchaseOrderRow[])
        : [],
    [relatedPurchaseOrders]
  );

  // 削除mutation
  const deleteMutation = trpc.suppliers.delete.useMutation({
    onSuccess: () => {
      messageApi.success('業者を削除しました');
      navigate('/app/suppliers');
    },
    onError: (error) => {
      messageApi.error(`削除に失敗しました: ${error.message}`);
    },
  });

  // 削除確認
  const handleDelete = () => {
    if (!supplier) return;

    Modal.confirm({
      title: '業者の削除',
      content: `「${supplier.name}」を削除してもよろしいですか？この操作は取り消せません。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        deleteMutation.mutate({ id: supplier.id });
      },
    });
  };

  // 編集モーダルを開く
  const handleEdit = () => {
    setIsEditModalVisible(true);
  };

  // モーダル成功
  const handleModalSuccess = () => {
    refetch();
    setIsEditModalVisible(false);
  };

  // ドロップダウンメニュー
  const actionMenuItems: MenuProps['items'] = [
    {
      key: 'delete',
      icon: canManage ? <DeleteOutlined /> : <LockOutlined />,
      label: canManage ? '削除' : (
        <Tooltip title={getDisabledMessage('manage')}>
          <span>削除</span>
        </Tooltip>
      ),
      danger: canManage,
      disabled: deleteMutation.isPending || !canManage,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      handleDelete();
    }
  };

  // 関連設備のカラム定義
  const equipmentColumns: ColumnsType<RelatedEquipmentRow> = [
    {
      title: '設備名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string | null | undefined, record: RelatedEquipmentRow) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/equipments/${record.id}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'カテゴリー',
      dataIndex: 'category',
      key: 'category',
      render: (category: CategorySummary | null | undefined) => (
        <CategoryTag category={category} linkPath="/app/equipments" />
      ),
    },
    {
      title: '役割',
      dataIndex: 'supplierRole',
      key: 'supplierRole',
      render: (role?: string | null) => <Tag color="blue">{role ?? '-'}</Tag>,
    },
  ];

  // 関連部品・消耗品のカラム定義
  const partColumns: ColumnsType<RelatedPartRow> = [
    {
      title: '部品・消耗品名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string | null | undefined, record: RelatedPartRow) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/parts/${record.id}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: '型式',
      dataIndex: 'partNumber',
      key: 'partNumber',
      render: (partNumber?: string) => partNumber || '-',
    },
    {
      title: 'カテゴリー',
      dataIndex: 'category',
      key: 'category',
      render: (category: CategorySummary | null | undefined) => (
        <CategoryTag category={category} linkPath="/app/parts" />
      ),
    },
    {
      title: '役割',
      dataIndex: 'supplierRole',
      key: 'supplierRole',
      render: (role?: string | null) => <Tag color="blue">{role ?? '-'}</Tag>,
    },
    {
      title: '現在在庫',
      dataIndex: 'currentStockLevel',
      key: 'currentStockLevel',
      render: (count?: number | null) => {
        const safeCount = count ?? 0;
        return <Tag color={safeCount > 0 ? 'green' : 'red'}>{safeCount}</Tag>;
      },
    },
  ];

  // 関連発注のカラム定義
  const purchaseOrderColumns: ColumnsType<RelatedPurchaseOrderRow> = [
    {
      title: '発注番号',
      dataIndex: 'displayId',
      key: 'displayId',
      render: (text: string | null | undefined, record: RelatedPurchaseOrderRow) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/purchase-orders/${record.id}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: '発注日',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date?: string | null) => date ? new Date(date).toLocaleDateString('ja-JP') : '-',
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          draft: { label: '下書き', color: 'default' },
          ordered: { label: '発注済', color: 'blue' },
          partial: { label: '一部納品', color: 'orange' },
          delivered: { label: '納品済', color: 'green' },
          cancelled: { label: 'キャンセル', color: 'red' },
        };
        const statusInfo = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: '合計金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: string) => amount ? `¥${Number(amount).toLocaleString()}` : '-',
    },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!supplier) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text>業者が見つかりませんでした。</Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate('/app/suppliers')}>
              業者一覧に戻る
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {messageContextHolder}

      {/* ヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/app/suppliers')}
            >
              戻る
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {supplier.name}
            </Title>
          </Space>
          <Space size={8}>
            <PermissionButton
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEdit}
              style={actionButtonStyle}
              requiredPermission="manage"
            >
              編集
            </PermissionButton>
            <Dropdown
              menu={{ items: actionMenuItems, onClick: handleMenuClick }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="default"
                icon={<MoreOutlined />}
                aria-label="その他の操作"
                loading={deleteMutation.isPending}
                style={{
                  width: 40,
                  height: 40,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  borderColor: '#d9d9d9',
                }}
              />
            </Dropdown>
          </Space>
        </Space>
      </div>

      {/* 基本情報 */}
      <Card title="基本情報" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="業者名" span={2}>
            <Text strong>{supplier.name}</Text>
          </Descriptions.Item>
          {supplier.nameKana && (
            <Descriptions.Item label="業者名（カナ）" span={2}>
              <Text type="secondary">{supplier.nameKana}</Text>
            </Descriptions.Item>
          )}
          {supplier.category && (
            <Descriptions.Item label="カテゴリ" span={2}>
              <CategoryTag category={supplier.category} />
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* 連絡先情報 */}
      <Card title="連絡先情報" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          {supplier.contactPerson && (
            <Descriptions.Item label="担当者" span={1}>
              <Space>
                <UserOutlined />
                {supplier.contactPerson}
              </Space>
            </Descriptions.Item>
          )}
          {supplier.phone && (
            <Descriptions.Item label="電話番号" span={1}>
              <Space>
                <PhoneOutlined />
                <a href={`tel:${supplier.phone}`}>{supplier.phone}</a>
              </Space>
            </Descriptions.Item>
          )}
          {supplier.mobilePhone && (
            <Descriptions.Item label="携帯電話" span={1}>
              <Space>
                <PhoneOutlined />
                <a href={`tel:${supplier.mobilePhone}`}>{supplier.mobilePhone}</a>
              </Space>
            </Descriptions.Item>
          )}
          {supplier.email && (
            <Descriptions.Item label="メールアドレス" span={1}>
              <Space>
                <MailOutlined />
                <a href={`mailto:${supplier.email}`}>{supplier.email}</a>
              </Space>
            </Descriptions.Item>
          )}
          {supplier.fax && (
            <Descriptions.Item label="FAX" span={1}>
              {supplier.fax}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* 住所情報 */}
      {(supplier.postalCode || supplier.address) && (
        <Card title="住所情報" style={{ marginBottom: 16 }}>
          <Descriptions column={1} bordered>
            {supplier.postalCode && (
              <Descriptions.Item label="郵便番号">
                〒{supplier.postalCode}
              </Descriptions.Item>
            )}
            {supplier.address && (
              <Descriptions.Item label="住所">
                <Space>
                  <EnvironmentOutlined />
                  {supplier.address}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* 管理情報 */}
      <Card title="管理情報" style={{ marginBottom: 16 }}>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="担当ユーザー">
            {supplier.manager ? (
              <UserDisplay user={supplier.manager} />
            ) : (
              <Text type="secondary">未設定</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 備考 */}
      <Card title="備考" style={{ marginBottom: 16 }}>
        <Text>{supplier.notes || '-'}</Text>
      </Card>

      {/* システム情報 */}
      <Card title="システム情報" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="登録日">
            {new Date(supplier.createdAt).toLocaleString('ja-JP')}
          </Descriptions.Item>
          <Descriptions.Item label="更新日">
            {new Date(supplier.updatedAt).toLocaleString('ja-JP')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        defaultActiveKey="equipments"
        items={[
          {
            key: 'equipments',
            label: '関連設備',
            children: (
              <Card>
                <Table
                  columns={equipmentColumns}
                  dataSource={equipmentRows}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'この業者に関連する設備はありません' }}
                />
              </Card>
            ),
          },
          {
            key: 'parts',
            label: '関連部品・消耗品',
            children: (
              <Card>
                <Table
                  columns={partColumns}
                  dataSource={partRows}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'この業者に関連する部品・消耗品はありません' }}
                />
              </Card>
            ),
          },
          {
            key: 'orders',
            label: '関連発注',
            children: (
              <Card>
                <Table
                  columns={purchaseOrderColumns}
                  dataSource={purchaseOrderRows}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'この業者への発注はありません' }}
                />
              </Card>
            ),
          },
          {
            key: 'documents',
            label: '書類',
            children: (
              <Card>
                <DocumentListSection
                  entityType="supplier"
                  entityId={supplier.id}
                  allowCreate={true}
                  variant="plain"
                />
              </Card>
            ),
          },
        ]}
      />

      {/* 編集モーダル */}
      <SupplierFormModal
        open={isEditModalVisible}
        supplier={supplier}
        onClose={() => setIsEditModalVisible(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}

export default SupplierDetailPage;
